import React, { useCallback, useRef, useState } from 'react';

import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import type { PurchaseOrder } from '../types';
import { fieldErrorsOf } from '../hooks/useServerErrors';
import { ActionDialog, ReasonModal } from './dialogs';

/** Only DRAFT; TRANSFER POs are born CONFIRMED and never show Confirm. */
const canConfirm = (po: PurchaseOrder) =>
  po.status === 'DRAFT' && po.type !== 'TRANSFER';

/**
 * DRAFT, or CONFIRMED with nothing received yet (409 PO_HAS_RECEIPTS
 * otherwise). Never a TRANSFER PO: its goods already left the source hub, so
 * it is cancelled through its outbound order (409 TRANSFER_PO_NOT_CANCELLABLE).
 */
const canCancel = (po: PurchaseOrder) =>
  po.type !== 'TRANSFER' &&
  (po.status === 'DRAFT' ||
    (po.status === 'CONFIRMED' && po.totalReceived === 0));

interface PurchaseOrderActionsOptions {
  /**
   * `inbound`: the screen is the inbound request — the cancel dialog, its
   * toasts and its copy talk about the inbound request, not the PO.
   */
  as?: 'po' | 'inbound';
}

/**
 * Confirm / cancel a PO from the list or the detail page, each behind its
 * dialog (`requestConfirm` / `requestCancel`; render `dialogs` once). A
 * second Confirm while one is in flight is ignored; after a mutation
 * `onChanged` reloads what the page shows.
 */
export function usePurchaseOrderActions(
  onChanged: () => void,
  { as = 'po' }: PurchaseOrderActionsOptions = {}
) {
  const inbound = as === 'inbound';
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const confirmingRef = useRef<string | null>(null);
  const [busyID, setBusyID] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrder | null>(null);
  const cancelRef = useRef(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<PurchaseOrder | null>(
    null
  );

  const confirm = useCallback(async (po: PurchaseOrder) => {
    if (confirmingRef.current) return;
    confirmingRef.current = po.id;
    setBusyID(po.id);
    try {
      await warehouseApi.confirmPurchaseOrder(po.id);
      toast.success('PO confirmed', { description: po.code });
      onChangedRef.current();
    } catch (err) {
      toast.error('Gagal confirm PO', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      confirmingRef.current = null;
      setBusyID(null);
    }
  }, []);

  const doCancel = async (reason: string) => {
    if (!cancelTarget || cancelRef.current) return;
    cancelRef.current = true;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await warehouseApi.cancelPurchaseOrder(cancelTarget.id, reason);
      toast.info(inbound ? 'Inbound request dibatalkan' : 'PO dibatalkan', {
        description: cancelTarget.code,
      });
      setCancelTarget(null);
      onChangedRef.current();
    } catch (err) {
      const reasonErr = fieldErrorsOf(err).list.find(
        (x) => x.field === 'reason'
      );
      if (reasonErr) setCancelError(reasonErr.message);
      else
        toast.error(
          inbound
            ? 'Gagal membatalkan inbound request'
            : 'Gagal membatalkan PO',
          { description: parseWarehouseError(err).message }
        );
    } finally {
      cancelRef.current = false;
      setCancelBusy(false);
    }
  };

  const requestCancel = useCallback((po: PurchaseOrder) => {
    setCancelError(null);
    setCancelTarget(po);
  }, []);

  const dialogs = (
    <>
      <ActionDialog
        open={!!confirmTarget}
        title="Confirm PO"
        subtitle={
          confirmTarget
            ? `${confirmTarget.code} · ${confirmTarget.supplierName}`
            : undefined
        }
        description="Inbound request terbit dan PO tidak bisa diubah lagi."
        confirmLabel="Confirm PO"
        busy={!!confirmTarget && busyID === confirmTarget.id}
        testId="po-confirm-dialog"
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => {
          const po = confirmTarget;
          if (po) void confirm(po).then(() => setConfirmTarget(null));
        }}
      />
      <ReasonModal
        open={!!cancelTarget}
        title={inbound ? 'Batalkan inbound request' : 'Cancel PO'}
        subtitle={
          cancelTarget
            ? `${cancelTarget.code} · ${cancelTarget.supplierName}`
            : undefined
        }
        notice={
          inbound
            ? 'Inbound request ditutup dan tidak menunggu barang lagi. PO-nya ikut dibatalkan.'
            : cancelTarget?.status === 'CONFIRMED'
              ? 'PO dibatalkan dan inbound request ditutup.'
              : 'PO dibatalkan.'
        }
        confirmLabel={inbound ? 'Batalkan inbound request' : 'Cancel PO'}
        busy={cancelBusy}
        error={cancelError}
        testId={inbound ? 'inbound-cancel-dialog' : 'po-cancel-dialog'}
        onConfirm={doCancel}
        onClose={() => setCancelTarget(null)}
      />
    </>
  );

  return {
    confirm,
    /** Ask, then confirm the PO. */
    requestConfirm: setConfirmTarget,
    requestCancel,
    canConfirm,
    canCancel,
    /** PO whose Confirm is in flight. */
    busyID,
    dialogs,
  };
}
